import { useEffect, useState, useMemo, useCallback, createContext, useContext } from 'react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Globe } from 'lucide-react';
import { getFxRates } from '@/lib/api';

const STORAGE_KEY = 'iariya:currency';

const FxContext = createContext({
  currency: 'NGN',
  symbol: '₦',
  rate: 1.0,
  rates: { NGN: 1.0 },
  currencies: [{ code: 'NGN', symbol: '₦', label: 'Naira' }],
  setCurrency: () => {},
  format: (ngn) => `₦${(ngn || 0).toLocaleString()}`,
  ready: false,
});

/**
 * Wraps any subtree that needs multi-currency price rendering. Caches the FX
 * doc at mount; persists the user's choice to localStorage so the picker
 * remembers across page navigations.
 */
export function FxProvider({ children }) {
  const [data, setData] = useState({ rates: { NGN: 1.0 }, currencies: [{ code: 'NGN', symbol: '₦', label: 'Naira' }], ready: false });
  const [currency, setCurrencyState] = useState(() => {
    if (typeof window === 'undefined') return 'NGN';
    return localStorage.getItem(STORAGE_KEY) || 'NGN';
  });

  useEffect(() => {
    let alive = true;
    getFxRates()
      .then(r => { if (alive) setData({ ...r.data, ready: true }); })
      .catch(() => { if (alive) setData(prev => ({ ...prev, ready: true })); });
    return () => { alive = false; };
  }, []);

  const setCurrency = useCallback((c) => {
    setCurrencyState(c);
    if (typeof window !== 'undefined') localStorage.setItem(STORAGE_KEY, c);
  }, []);

  const symbol = useMemo(() => {
    const c = data.currencies.find(c => c.code === currency);
    return c?.symbol || '₦';
  }, [data.currencies, currency]);

  const rate = useMemo(() => Number(data.rates?.[currency] || 1), [data.rates, currency]);

  const format = useCallback((ngn) => {
    const amount = Number(ngn || 0);
    if (currency === 'NGN' || rate <= 0) {
      return `₦${amount.toLocaleString('en-NG')}`;
    }
    const converted = amount / rate;
    return `${symbol}${converted.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  }, [currency, rate, symbol]);

  const value = useMemo(() => ({
    currency,
    symbol,
    rate,
    rates: data.rates,
    currencies: data.currencies,
    setCurrency,
    format,
    ready: data.ready,
  }), [currency, symbol, rate, data, setCurrency, format]);

  return <FxContext.Provider value={value}>{children}</FxContext.Provider>;
}

export const useFx = () => useContext(FxContext);

/**
 * Compact currency dropdown for the storefront page header. Falls back gracefully
 * if FX data hasn't loaded yet.
 */
export default function CurrencySwitcher({ className = '' }) {
  const { currency, setCurrency, currencies, ready } = useFx();
  if (!ready || currencies.length <= 1) return null;
  return (
    <div className={`inline-flex items-center gap-1 ${className}`} data-testid="currency-switcher">
      <Globe className="h-3.5 w-3.5 text-muted-foreground" />
      <Select value={currency} onValueChange={setCurrency}>
        <SelectTrigger
          className="h-8 w-auto rounded-full text-xs px-3 border-neutral-200 bg-white/90 backdrop-blur"
          aria-label="Currency"
          data-testid="currency-switcher-trigger"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {currencies.map(c => (
            <SelectItem key={c.code} value={c.code} data-testid={`currency-option-${c.code}`}>
              <span className="font-mono mr-1">{c.symbol}</span> {c.code} <span className="text-muted-foreground ml-1 text-[10px]">{c.label}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
